import { Component, computed, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService } from '@eforall/dashboard';
import { AdmissionsMatchingFuncData } from '@interfaces';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import { FuncService } from '../../../../../../../services';
import { initializeMatchData } from '../match-data/initialize-match-data';
import { MatchedAcc, MatchedDonorGeoMentor, MatchedTeam, MatchedTrimester } from '../match-data/match-data-interfaces';
import { MatcherDialogData } from './matcher-dialog-data';
import { MatchWorkerDataIn, MatchWorkerDataOut } from './worker/matcher-worker-interfaces';


@Component({
	selector: 'matcher-dialog',
	standalone: true,
	imports: [DashboardDialogFramePart, TooltipsModule],
	templateUrl: './matcher.dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./matcher.dialog.scss'],
})
export class MatcherDialog implements OnDestroy {

	private readonly func = inject(FuncService);
	public readonly dialogService = inject(DialogService);
	public readonly dialog = inject<DialogContext<MatcherDialogData>>(MAT_DIALOG_DATA);
	public readonly dialogRef = inject(MatDialogRef<MatcherDialog>);

	runActionText = signal('Start');

	runAction: DialogAction = {
		id: 'start',
		enabled: signal(true),
		visible: signal(true),
		cssClass: signal(undefined),
		text: this.runActionText,
		linkType: 'callback',
		callback: async () => { this.startButtonClick() },
		willCloseDialog: false,
	};

	applyActionVisible = signal(false);

	applyAction: DialogAction = {
		id: 'apply',
		enabled: signal(true),
		visible: this.applyActionVisible,
		cssClass: signal(undefined),
		text: signal('Apply Match Results'),
		linkType: 'callback',
		callback: async () => { this.applyMatchResults() },
		willCloseDialog: false,
	};

	cancelActionVisible = signal(true);

	cancelAction: DialogAction = {
		id: 'cancel',
		enabled: signal(true),
		visible: this.cancelActionVisible,
		cssClass: signal(undefined),
		text: signal('Close Without Saving'),
		linkType: 'no-action',
		willCloseDialog: true,
	};

	actions: DialogAction[] = [this.runAction, this.applyAction, this.cancelAction];

	icon = signal('far fa-square');
	header = signal('Matcher');

	private readonly matcher: Worker;

	public readonly funcData = signal<AdmissionsMatchingFuncData | undefined>(undefined);
	public readonly matchData = signal<MatchedTrimester | undefined>(undefined);
	public readonly teamByVolunteerSignupMentorId = signal<Map<number, MatchedTeam> | undefined>(undefined);
	public readonly progress = signal({ attempts: 0, swaps: 0 });

	/** Toggled by the user by clicking the run button */
	private readonly paused = computed(() => this.runAction.text() !== 'Pause');

	/** True while a batch of swaps is running in the web worker  */
	private readonly batching = signal(false);

	private run: 'Test' | 'Live' = 'Test';


	constructor() {
		if (typeof Worker == 'undefined') throw new Error(`Web Workers are not supported in this environment.`);
		if (!this.dialog.data) throw new Error(`Matcher Dialog launched without data`);

		this.matcher = new Worker(new URL('./worker/matcher.worker', import.meta.url));
		this.matcher.onmessage = this.onMatcherCallback;

		const { run, data } = this.dialog.data!;

		this.setup(run, data);
	}


	ngOnDestroy(): void {
		if (this.matcher) this.matcher.terminate();
	}


	private async setup(run: 'Test' | 'Live', data: AdmissionsMatchingFuncData) {

		this.run = run;
		this.header.set(run + ' Matcher');

		const initial = await initializeMatchData(run, data, this.dialogService);

		this.funcData.set(data);
		this.matchData.set(initial.matchData);
		this.teamByVolunteerSignupMentorId.set(initial.teamByVolunteerSignupMentorId);

		const message: MatchWorkerDataIn = {
			action: 'Initialize',
			data: this.matchData()!,
			teamByVolunteerSignupMentorId: this.teamByVolunteerSignupMentorId()!,
			mentorLanguages: this.dialog.data!.data.mentors.map(m => ({ volunteerSignupMentorId: m.volunteerSignupMentorId, languageId: m.languageId })),
			zips: data.zips,
		};

		this.matcher.postMessage(message);
	}


	private async startButtonClick() {
		if (this.paused()) {

			this.runActionText.set('Pause');
			this.applyActionVisible.set(false);
			this.cancelActionVisible.set(false);

			if (this.batching() == false) {
				this.batching.set(true);

				const data = this.matchData()!;
				const funcData = this.funcData()!;

				const message: MatchWorkerDataIn = {
					action: 'Batch-Run',
					data,
					teamByVolunteerSignupMentorId: this.teamByVolunteerSignupMentorId()!,
					mentorLanguages: this.dialog.data!.data.mentors.map(m => ({ volunteerSignupMentorId: m.volunteerSignupMentorId, languageId: m.languageId })),
					zips: funcData.zips,
				};

				this.matcher.postMessage(message);
			}
		}
		else {
			this.runActionText.set('Continue');
			this.applyActionVisible.set(this.run == 'Live');
			this.cancelActionVisible.set(true);
		}
	}

	private async applyMatchResults() {

		const data = this.matchData();
		if (!data) return;

		if (data.canApply === false) {
			await this.dialogService.showMessage('Cannot Apply Match Results', 'All teams must have at least one overlapping meeting');
			return;
		}

		const yes = await this.dialogService.confirm('Would you like to apply these match results. The following steps will be performed', 'Apply', 'Cancel', [
			`The teams, ventures and mentors will be created according to the matching results.`,
			`All open applications will be 'closed' and deferred.`,
			`Trimester (${data.trimesterId}) will be marked as 'Admissions closed'.`
		]);

		if (!yes) return;

		await this.func.admissions.matching.applyResults({
			trimesterId: data.trimesterId,
			data: data.accs.map(acc => ({
				accId: acc.accId,
				languageId: acc.languageId,
				teams: acc.teams.map(team => ({
					applicationIds: team.people.ventures.map(v => v.accApplicationId),
					mentorSignupMentorIds: team.people.mentors.map(m => m.volunteerSignupMentorId),
					numMeetings: team.people.overlappingMeetings,
					monToSun: team.people.monToSun,
					matchingDetails: team.people.matchingSummary,
				}))
			}))
		});

		this.dialogRef.close(this.applyAction);
	}


	private readonly onMatcherCallback = (event: MessageEvent<MatchWorkerDataOut>) => {

		const payload = event.data;
		const d2 = payload.data;		// d2 = new data
		let d1 = this.matchData();	// d1 = current data

		let useNewData = false;

		if (d1 == undefined) {
			//
			// Existing data is undefined initially.  We must take the first new one.
			//
			useNewData = true;
		}
		else if (d1.teamsWithoutAMeeting == 0 && d2.teamsWithoutAMeeting > 0) {
			//
			// We cannot go from all teams having meetings to some having missing meetings
			//
			useNewData = false;
		}
		else if (d1.teamsWithoutAMeeting > 0 && d2.teamsWithoutAMeeting == 0) {
			//
			// If we had missing meetings but the new has meetings for all teams, then take it
			//
			useNewData = true;
		}
		else if (d2.donorGeoMentors.length !== d1.donorGeoMentors.length) {
			//
			// Don't use new data if fewer mentors with the flag
			// Always use new data if more mentors with the flag
			//
			useNewData = d2.donorGeoMentors.length > d1.donorGeoMentors.length;
		}
		else if (d2.donorGeoScore !== d1.donorGeoScore) {
			//
			// Don't use new data if donor geo score is lower
			// Always use new data if donor geo score is greater
			//
			useNewData = d2.donorGeoScore > d1.donorGeoScore;
		}
		else if (d2.lowestTeamScorePct > d1.lowestTeamScorePct) {
			//
			// Finally, use the new data if the lowest team score is better
			//
			useNewData = true;
		}


		//
		// Apply new data
		//
		if (useNewData) {
			this.matchData.set(d2);
			d1 = d2;
			this.teamByVolunteerSignupMentorId.set(payload.teamByVolunteerSignupMentorId);
		}

		if (payload.action == 'Initialize') return;

		const progress = this.progress();
		this.progress.set({ attempts: progress.attempts + 1, swaps: progress.swaps + (useNewData ? payload.swaps : 0) });


		if (this.paused()) {
			this.batching.set(false);
		}
		else {

			const message: MatchWorkerDataIn = {
				action: 'Batch-Run',
				data: this.matchData()!,			// We know it is defined because of the check up above
				teamByVolunteerSignupMentorId: this.teamByVolunteerSignupMentorId()!,
				mentorLanguages: this.dialog.data!.data.mentors.map(m => ({ volunteerSignupMentorId: m.volunteerSignupMentorId, languageId: m.languageId })),
				zips: this.funcData()!.zips,
			};

			this.matcher.postMessage(message) // Keep going indefinitely until paused}
		};
	}


	public readonly attempts = computed(() => {
		const p = this.progress();
		return `Attempts: ` + (p.attempts / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'M';
	});


	public readonly swaps = computed(() => {
		const p = this.progress();
		return `Total Swaps: ` + (p.swaps).toLocaleString();
	});


	public readonly noMeetings = computed(() => {
		const d = this.matchData();
		return `Teams Without Meetings: ` + (d ? d.teamsWithoutAMeeting.toLocaleString() : '?');
	});


	public readonly geoDonorScore = computed(() => {

		const header = `Geo Donor Score: `;

		const d = this.matchData();
		if (!d) return header + `?`;

		const donorsWithoutMeetings = d.donorGeoMentors.filter(m => m.overlappingMeetings == 0);
		if (donorsWithoutMeetings.length) return header + 'FAIL';

		const donorsTooFarAway = d.donorGeoMentors.filter(m => m.mentor.milesToClosestEntrepreneur >= 100);
		if (donorsTooFarAway.length) return header + 'FAIL';

		return header + d.donorGeoScore.toLocaleString('en-US', {
			minimumFractionDigits: 1,
			maximumFractionDigits: 1,
		}) + '%';
	});


	public readonly teamScore = computed(() => {
		const d = this.matchData();
		if (!d) return `Team Score: ?`;
		return `Team Score: ` + d.lowestTeamScorePct.toLocaleString('en-US', {
			minimumFractionDigits: 1,
			maximumFractionDigits: 1,
		}) + '%';
	});


	public getAccName(acc: MatchedAcc) {
		if (acc.hasMentors) return acc.accName + ' - Has Mentors';
		else return acc.accName;
	}


	public getGeoDonorTooltip(donorGeo: MatchedDonorGeoMentor) {

		const mentor = donorGeo.mentor;
		const name = 'Name: ' + mentor.name + '\nLocation: ' + mentor.location + '\n';
		const meetings = donorGeo.overlappingMeetings
			? `\nTeam has ${donorGeo.overlappingMeetings} overlapping meeting(s)`
			: `\nTeam has NO OVERLAPPING MEETINGS`;

		const miles = mentor.milesToClosestEntrepreneur;

		if (miles <= 0) return name + `Closest entrepreneur within the SAME ZIP CODE` + meetings;
		if (miles >= 100) return name + `Closest entrepreneur BEYOND 100 MILES away` + meetings;
		if (miles >= 99) return name + `Closest entrepreneur within the SAME STATE` + meetings;
		return name + `Closest entrepreneur WITHIN ${miles} MILES` + meetings;
	}


	public geoDonorDistances = computed(() => {
		const data = this.matchData();
		if (!data) return [];
		return data.donorGeoMentors.map(m => m.mentor.milesToClosestEntrepreneur);
	});
}